import { put, select, all, takeEvery, call } from 'redux-saga/effects';
import { EffectR } from 'types/utils';
import { getActiveTintColor, getSelectedTintColors } from 'features/product-info';
import { getBaseEntityIdForTintService } from 'features/related-services';
import { TTintColorWithQuantity } from 'features/product-info/components/pdp-select-tint-color';
import { getIsTintServiceAvailableInRegion } from 'features/page-model';
import { getYandexSplitSession } from 'features/yandex-split-banner/selectors';
import { YaPaySession } from 'types/yandex-pay';
import { BasketItem } from '../types';
import { getCartItem, getProductQuantityAccordingToSelectedServices } from './selectors';
import {
  BASKET_FLOW_LOAD,
  BASKET_FLOW_RESET_ITEM,
  BASKET_FLOW_UPDATE,
  BASKET_FLOW_UPDATE_ITEM,
  BASKET_SET,
  BASKET_UPDATE_ITEM,
  BasketFlowResetItemPayload,
  BasketFlowUpdateItemPayload,
  BasketUpdatePayload,
  UPDATE_TINT_SERVICE_IN_SERVICE_BASKET,
} from './actions';
import { handleUpdateTintServiceInServiceBasket } from './handle-update-tint-service-in-service-basket';
import { BASKET_LOAD, BASKET_SET_PRODUCT } from '../constants';

export function* rootSaga() {
  yield all([
    takeEvery(BASKET_FLOW_UPDATE_ITEM, handleBasketItemFlow),
    takeEvery(BASKET_FLOW_RESET_ITEM, handleBasketResetItemFlow),
    takeEvery(UPDATE_TINT_SERVICE_IN_SERVICE_BASKET, handleUpdateTintServiceInServiceBasket),
    takeEvery(BASKET_FLOW_LOAD, handleBasketLoad),
    takeEvery(BASKET_FLOW_UPDATE, handleBasketUpdate),
  ]);
}

export function* handleBasketItemFlow({ payload }: { payload: BasketFlowUpdateItemPayload }) {
  const { productId, flowQty } = payload;
  const item: BasketItem | undefined = (yield call(selectCartItem, productId)) as EffectR.Call<
    typeof selectCartItem
  >;
  const currentQty = item?.quantity ?? 0;
  const quantity = Number((currentQty + flowQty).toFixed(2));

  document.body.dispatchEvent(
    new CustomEvent(BASKET_SET_PRODUCT, { detail: { code: productId, quantity } }),
  );
  yield put(BASKET_UPDATE_ITEM({ code: productId, quantity }));

  const yandexSplitSession: YaPaySession = yield select(getYandexSplitSession);

  if (currentQty && yandexSplitSession) {
    yandexSplitSession.update(async () => {
      const splitQuantity = quantity || 1;
      const totalAmount = Number(yandexSplitSession.paymentData.totalAmount) * splitQuantity;
      return { totalAmount: String(totalAmount) };
    });
  }

  const isTintServiceAvailableInRegion: boolean = yield select(getIsTintServiceAvailableInRegion);

  if (isTintServiceAvailableInRegion) {
    const activeTintColor: TTintColorWithQuantity | null = yield select(getActiveTintColor);
    const isUserChangesTintServiceForProduct = activeTintColor !== null;

    const serviceIdForTint: string | null = yield select(getBaseEntityIdForTintService);
    const isServiceCanBeAddedToProduct = serviceIdForTint !== null;

    if (isUserChangesTintServiceForProduct && isServiceCanBeAddedToProduct) {
      const { HEX, COLOR_NAME, serviceQuantity } = activeTintColor;

      yield put(
        UPDATE_TINT_SERVICE_IN_SERVICE_BASKET({
          parentLineId: productId,
          flowQty,
          hex: HEX,
          color: COLOR_NAME,
          serviceIdForTint,
          currentServiceQuantity: serviceQuantity,
        }),
      );
    }
  }
}

export function* handleBasketResetItemFlow({ payload }: { payload: BasketFlowResetItemPayload }) {
  const { productId, context } = payload;
  const item = (yield call(selectCartItem, productId)) as EffectR.Call<typeof selectCartItem>;
  const currentQty = item?.quantity ?? 0;

  const isTintServiceAvailableInRegion: boolean = yield select(getIsTintServiceAvailableInRegion);

  const selectedTintColors: TTintColorWithQuantity[] | never[] = yield select(getSelectedTintColors);

  let flowQty;

  if (isTintServiceAvailableInRegion && selectedTintColors.length > 0) {
    const quantityOfSelectedColor: number = yield select(
      getProductQuantityAccordingToSelectedServices(productId),
    );

    flowQty = -quantityOfSelectedColor;
  } else {
    flowQty = -currentQty;
  }

  yield put(BASKET_FLOW_UPDATE_ITEM({ productId, flowQty, context }));
}

export function* selectCartItem(productId: string) {
  return (yield select(getCartItem(productId))) as EffectR.Select<ReturnType<typeof getCartItem>>;
}

export function* handleBasketLoad() {
  document.body.dispatchEvent(new CustomEvent(BASKET_LOAD));
  yield;
}

export function* handleBasketUpdate({ payload }: { payload: BasketUpdatePayload }) {
  yield put(BASKET_SET(payload));
}
